import { cloneElement, useEffect, useState } from 'react'
import {
  Box,
  Card,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material'
import type { SimulationResults } from '../api/types'
import { formatEUR, formatEUR2, formatPct } from '../format'
import { Mode } from './resultsMode'
import { Stat } from './Stat'

const MIN_COLUMN_WIDTH = 150

/** Fewest rows that fit `width`, then the smallest column count for those rows, so rows stay balanced. */
function evenColumns(count: number, width: number) {
  const fit = Math.max(1, Math.floor(width / MIN_COLUMN_WIDTH))
  return Math.ceil(count / Math.ceil(count / fit))
}

export default function ResultsPanel({
  r,
  compact = false,
  mode: modeProp,
  onModeChange,
  hideToggle = false,
}: {
  r: SimulationResults
  compact?: boolean
  mode?: Mode
  onModeChange?: (mode: Mode) => void
  hideToggle?: boolean
}) {
  const isXs = useMediaQuery((t) => t.breakpoints.down('md'))
  const [internalMode, setInternalMode] = useState<Mode>(Mode.Rental)
  const mode = modeProp ?? internalMode
  const setMode = onModeChange ?? setInternalMode

  const toggle = (
    <ToggleButtonGroup
      value={mode}
      exclusive
      size="small"
      onChange={(_, value) => value && setMode(value)}
    >
      <ToggleButton value={Mode.Rental}>Location</ToggleButton>
      <ToggleButton value={Mode.Resale}>Revente</ToggleButton>
    </ToggleButtonGroup>
  )

  const [gridEl, setGridEl] = useState<HTMLElement | null>(null)
  const [gridWidth, setGridWidth] = useState(0)
  useEffect(() => {
    if (!gridEl) return
    const observer = new ResizeObserver(([entry]) => setGridWidth(entry.contentRect.width))
    observer.observe(gridEl)
    return () => observer.disconnect()
  }, [gridEl])

  const stats =
    mode === Mode.Rental
      ? [
          <Stat
            key="cashflow"
            label={compact ? 'Cash-flow / mois' : 'Cash-flow mensuel'}
            value={formatEUR2(r.monthlyCashFlow)}
            tone={r.monthlyCashFlow >= 0 ? 'positive' : 'negative'}
            hint={compact ? undefined : `${formatEUR(r.annualCashFlow)} / an`}
          />,
          <Stat key="brut" label="Rendement brut" value={formatPct(r.grossYield)} />,
          <Stat
            key="net"
            label={compact ? 'Rendement net' : 'Rendement net de charge'}
            value={formatPct(r.netYield)}
          />,
          <Stat key="fin" label="Rendement financier" value={formatPct(r.financialYield)} />,
          ...(compact
            ? []
            : [
                <Stat
                  key="mensualite"
                  label="Mensualité crédit"
                  value={formatEUR2(r.monthlyLoanPayment)}
                />,
                <Stat
                  key="cout"
                  label="Coût de l'investissement"
                  value={formatEUR(r.investmentCost)}
                />,
              ]),
        ]
      : [
          <Stat
            key="estimation"
            label="Estimation de revente"
            value={formatEUR(r.resaleEstimate)}
          />,
          <Stat
            key="margeBrute"
            label="Marge brute"
            value={`${formatEUR(r.grossMargin)} (${formatPct(r.grossMarginPercent)})`}
            tone={r.grossMargin >= 0 ? 'positive' : 'negative'}
          />,
          <Stat
            key="margeAvantImpots"
            label="Marge nette avant impôts"
            value={`${formatEUR(r.netMarginBeforeTax)} (${formatPct(r.netMarginChargePercent)})`}
            tone={r.netMarginBeforeTax >= 0 ? 'positive' : 'negative'}
          />,
          <Stat
            key="marge"
            label="Marge nette"
            value={formatEUR(r.netMargin)}
            tone={r.netMargin >= 0 ? 'positive' : 'negative'}
          />,
          <Stat key="roi" label="ROI du projet" value={formatPct(r.roi)} />,
          <Stat
            key="annualise"
            label="Rentabilité annualisée"
            value={formatPct(r.annualizedReturn)}
          />,
        ]

  const columns = evenColumns(stats.length, gridWidth)
  const statsRow = isXs ? (
    <Stack divider={<Divider />}>{stats.map((el) => cloneElement(el, { inline: true }))}</Stack>
  ) : (
    <Box
      ref={setGridEl}
      sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {stats.map((el, i) => (
        <Box
          key={el.key}
          sx={{
            display: 'flex',
            borderLeft: i % columns === 0 ? 0 : 1,
            borderTop: i < columns ? 0 : 1,
            borderColor: 'divider',
          }}
        >
          {el}
        </Box>
      ))}
    </Box>
  )

  if (compact) {
    return (
      <div>
        {!hideToggle && <Box sx={{ mb: 1.5 }}>{toggle}</Box>}
        {statsRow}
      </div>
    )
  }

  return (
    <Card>
      {!hideToggle && (
        <>
          <Stack
            direction="row"
            sx={{
              px: 2,
              py: 1.5,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Rentabilité
            </Typography>
            {toggle}
          </Stack>
          <Divider />
        </>
      )}
      {statsRow}
    </Card>
  )
}
